// Stage-gate pipeline: horizontal connected stage chips (Intake → … → Retirement)
// for /lifecycle, plus a skewed-chevron variant for the gate-review header.
// stages: [{ id, label, value?, count?, status?: 'done'|'active'|'upcoming'|'blocked', ribbon? }]

import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

export function StagePipeline({ stages = [], variant = 'pipeline', onSelect, selectedId }) {
  if (variant === 'chevron')
    return <ChevronPipeline stages={stages} onSelect={onSelect} selectedId={selectedId} />;
  return <DottedPipeline stages={stages} onSelect={onSelect} selectedId={selectedId} />;
}

function DottedPipeline({ stages, onSelect, selectedId }) {
  const tooltip = useOptionalRichTooltipController();
  return (
    <div className="stage-pipeline" role="list">
      <div className="stage-connector" aria-hidden />
      {stages.map((s, i) => {
        const status = s.status || 'upcoming';
        const isSelected = selectedId === s.id;
        const isInteractive = Boolean(onSelect || (s.richTooltip && tooltip));
        const StageNode = isInteractive ? 'button' : 'div';
        const triggerProps =
          s.richTooltip && tooltip
            ? tooltip.getTriggerProps({
                surfaceId: s.richTooltip.surfaceId,
                payload: s.richTooltip,
                testIdPrefix: 'rich-tooltip',
                ariaLabel: `${s.label} stage details`,
                onClick: () => onSelect && onSelect(s),
              })
            : {};
        return (
          <StageNode
            key={s.id || i}
            className={`stage-node ${status} ${isSelected ? 'is-selected' : ''}`}
            role="listitem"
            onClick={!s.richTooltip ? () => onSelect && onSelect(s) : undefined}
            type={isInteractive ? 'button' : undefined}
            data-testid={`stage-node-${s.id}`}
            {...triggerProps}
          >
            <span className="stage-bubble" aria-hidden>
              {status === 'done' ? '✓' : status === 'active' ? i + 1 : i + 1}
            </span>
            <span className="stage-tile">
              <span className="stage-label">{s.label}</span>
              {s.count != null ? <span className="stage-count">{s.count}</span> : null}
              {s.value ? <span className="stage-value">{s.value}</span> : null}
            </span>
            {s.ribbon ? (
              <span className={`ribbon ${s.ribbon.tone || ''}`}>{s.ribbon.label}</span>
            ) : null}
          </StageNode>
        );
      })}
    </div>
  );
}

function ChevronPipeline({ stages, onSelect, selectedId }) {
  const tooltip = useOptionalRichTooltipController();
  return (
    <div className="chevron-pipeline" role="list">
      {stages.map((s, i) => {
        const status = s.status || 'upcoming';
        const isInteractive = Boolean(onSelect || (s.richTooltip && tooltip));
        const StageNode = isInteractive ? 'button' : 'div';
        const triggerProps =
          s.richTooltip && tooltip
            ? tooltip.getTriggerProps({
                surfaceId: s.richTooltip.surfaceId,
                payload: s.richTooltip,
                testIdPrefix: 'rich-tooltip',
                ariaLabel: `${s.label} gate details`,
                onClick: () => onSelect && onSelect(s),
              })
            : {};
        return (
          <StageNode
            key={s.id || i}
            className={`chevron-step ${status} ${selectedId === s.id ? 'is-selected' : ''}`}
            onClick={!s.richTooltip ? () => onSelect && onSelect(s) : undefined}
            role="listitem"
            type={isInteractive ? 'button' : undefined}
            data-testid={`stage-node-${s.id}`}
            {...triggerProps}
          >
            <span aria-hidden>{status === 'done' ? '✓' : i + 1}</span>
            <span>{s.label}</span>
          </StageNode>
        );
      })}
    </div>
  );
}
